import React, { useState, useMemo } from 'react'
import { TextField, Box, Typography, Chip, CircularProgress } from '@mui/material'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { useQuery, useMutation, gql } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { getRestaurantDetail } from '../../apollo'
import { GET_RESTAURANT_ITEMS } from '../../apollo/advancedMenuQueries'

import { mw, chipSx } from '../../views/menuWorkspaceStyles'

const GET_RESTAURANT_CATEGORIES = gql`
  ${getRestaurantDetail}
`

const CREATE_CATEGORY = gql`
  mutation CreateCategory($category: CategoryInput!) {
    createCategory(category: $category) {
      _id
      categories {
        _id
        title
        foods { _id }
      }
    }
  }
`

const filter = createFilterOptions()

// Categories are matched by title, never by _id: an item's saved categories
// come back as plain names (categoryName), so its chips carry the title as
// their _id while the dropdown options carry the real id. Comparing ids let
// the same category be picked twice ("CLASSICS, CLASSICS").
const categoryKey = (c) => String(typeof c === 'string' ? c : (c?.title ?? '')).trim().toLowerCase()

export const uniqueCategories = (list) => {
  const seen = new Set()
  return list.filter((c) => {
    const key = categoryKey(c)
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const CategoryMultiSelect = ({ selectedCategories, onChange, restaurantId }) => {
  const { t } = useTranslation()
  const [error, setError] = useState('')

  const { data, loading } = useQuery(GET_RESTAURANT_CATEGORIES, {
    variables: { id: restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'cache-and-network'
  })

  const [createCategory, { loading: creatingCategory }] = useMutation(CREATE_CATEGORY, {
    refetchQueries: [{ query: GET_RESTAURANT_CATEGORIES, variables: { id: restaurantId } }]
  })

  const categories = useMemo(() =>
    (data?.restaurant?.categories || []).slice().sort((a, b) =>
      (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' })),
    [data])

  // Item counts per category come from the items' categoryName (same source
  // the Categories tab uses) — the legacy foods relation is always empty for
  // advanced-menu items, which made every option show "0 items".
  const { data: itemsData } = useQuery(GET_RESTAURANT_ITEMS, {
    variables: { restaurantId },
    skip: !restaurantId
  })
  const itemCountByCategory = useMemo(() => {
    const map = {}
    for (const item of (itemsData?.advancedRestaurantItems || [])) {
      if (!item.categoryName) continue
      item.categoryName.split(',').forEach(c => {
        const key = c.trim().toLowerCase()
        if (key) map[key] = (map[key] || 0) + 1
      })
    }
    return map
  }, [itemsData])

  // Selection changes can contain: existing category objects, a raw string
  // (freeSolo text + Enter), or the injected { inputValue } "Create…" option.
  // Strings / create-options become a real category via the createCategory
  // mutation, so the new category also appears on the Categories tab.
  const handleChange = async (e, newValue) => {
    setError('')
    const picked = []
    let newTitle = null
    newValue.forEach((v) => {
      if (typeof v === 'string') newTitle = v.trim()
      else if (v && v.inputValue) newTitle = v.inputValue.trim()
      else if (v) picked.push(v)
    })

    if (!newTitle) {
      onChange(uniqueCategories(picked))
      return
    }

    // Typed name is already selected → nothing to add.
    if (picked.some(c => categoryKey(c) === categoryKey(newTitle))) {
      onChange(uniqueCategories(picked))
      return
    }

    // Typed name matches an existing category (case-insensitive) → select it.
    const existing = categories.find(c => categoryKey(c) === categoryKey(newTitle))
    if (existing) {
      picked.push(existing)
      onChange(uniqueCategories(picked))
      return
    }

    try {
      const res = await createCategory({
        variables: { category: { title: newTitle, restaurant: restaurantId } }
      })
      const created = (res?.data?.createCategory?.categories || [])
        .find(c => c.title.toLowerCase() === newTitle.toLowerCase())
      picked.push(created ? { _id: created._id, title: created.title } : { _id: newTitle, title: newTitle })
      onChange(uniqueCategories(picked))
    } catch (err) {
      setError(err.message)
      onChange(uniqueCategories(picked))
    }
  }

  return (
    <Box>
      <Autocomplete
        multiple
        freeSolo
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        options={categories}
        getOptionLabel={(opt) => {
          if (typeof opt === 'string') return opt
          if (opt.inputValue) return opt.inputValue
          return opt.title
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params)
          const input = params.inputValue.trim()
          if (
            input !== '' &&
            !options.some(o => o.title.toLowerCase() === input.toLowerCase())
          ) {
            filtered.push({ inputValue: input, title: input })
          }
          return filtered
        }}
        value={selectedCategories}
        onChange={handleChange}
        loading={loading || creatingCategory}
        disabled={creatingCategory}
        isOptionEqualToValue={(option, value) => categoryKey(option) === categoryKey(value)}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              key={option._id}
              label={option.title}
              size="small"
              sx={chipSx.category}
              {...getTagProps({ index })}
            />
          ))
        }
        renderOption={(props, option) => (
          <li {...props} key={option.inputValue ? `__create__${option.inputValue}` : option._id}>
            {option.inputValue ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AddCircleOutlineIcon fontSize="small" sx={{ color: mw.color.emerald }} />
                <Box>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: mw.color.emerald }}>
                    {t('Create "{{name}}"', { name: option.inputValue })}
                  </Typography>
                  <Typography variant="caption" sx={{ color: mw.color.textMuted }}>
                    {t('New category — will also appear on the Categories tab')}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box>
                <Typography variant="body2" fontWeight="bold" sx={{ color: mw.color.textSecondary }}>
                  {option.title}
                </Typography>
                <Typography variant="caption" sx={{ color: mw.color.textMuted }}>
                  {itemCountByCategory[option.title.trim().toLowerCase()] || 0} {t('items')}
                </Typography>
              </Box>
            )}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('Categories')}
            placeholder={t('Select or type to create')}
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {(loading || creatingCategory) ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
      />
      {error && (
        <Typography variant="caption" sx={{ color: mw.color.off, mt: 0.5, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  )
}

export default CategoryMultiSelect
